import { ResponsiveLine, Serie } from "@nivo/line";

import { EmptyChartCardComponent } from "@zenith/features/Analytics";
import { getSuffixedNumber } from "@zenith/utils";
import { getChartTheme } from "@zenith/utils/Helpers";
import { memo } from "react";

/* eslint-disable-next-line */
export interface LineChartComponentProps {
  data: Serie[];
}

const baseTheme = getChartTheme();

// Custom theme with tooltip styling
const theme = {
  ...baseTheme,
  tooltip: {
    container: {
      background: "#373737",
      color: "white",
      borderRadius: "8px",
      fontSize: "12px",
      padding: "8px 12px",
    },
  },
};

// Custom tooltip component
const CustomLineTooltip = ({ point }: { point: any }) => (
  <div className="rounded-lg bg-neutral-800 px-3 py-2 text-white shadow-lg">
    <p className="text-sm font-medium">
      {point.data.yFormatted}
    </p>
  </div>
);

const LineChartComponent = memo(function LineChartComponent(
  props: LineChartComponentProps
) {
  const { data } = props;
  const isEmptyValues = data[0]?.data.every(
    (item) => item.y === 0 || item.x === 0
  );

  return (
    <div className="flex rounded-lg border border-zinc-300 bg-white p-8">
      {!data[0].data.length || isEmptyValues ? (
        <EmptyChartCardComponent />
      ) : (
        <div
          className="flex h-full w-full"
          style={{ height: "24rem", maxWidth: 650 }}
        >
          <ResponsiveLine
            data={data}
            enablePointLabel
            pointLabel={(e) =>
              e.y && (e.y as number) > 0 ? getSuffixedNumber(e.y as number) : ""
            }
            margin={{ top: 20, right: 15, bottom: 55, left: 55 }}
            xScale={{ type: "point" }}
            yScale={{
              type: "linear",
              min: "auto",
              max: "auto",
              stacked: true,
              reverse: false,
            }}
            colors={"#DA291C"}
            axisLeft={{
              tickSize: 0,
              tickPadding: 10,
              ticksPosition: "before",
              format: function (value) {
                return getSuffixedNumber(value);
              },
            }}
            theme={theme}
            axisBottom={{
              legend: "Days",
              legendOffset: 45,
              legendPosition: "middle",
              tickSize: 0,
              tickPadding: 10,
            }}
            enableGridX={false}
            enableGridY={false}
            markers={[
              {
                axis: "y",
                value: 0,
                lineStyle: {
                  stroke: "#C6C6C6",
                  strokeWidth: "0.5",
                },
              },
            ]}
            pointSize={10}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            pointLabelYOffset={-12}
            useMesh={true}
            tooltip={({ point }) => <CustomLineTooltip point={point} />}
            role="application"
          />
        </div>
      )}
    </div>
  );
});

export default LineChartComponent;